import type { GetLsproBrandingSettingsQuery } from 'src/generated/graphql-manager';

import {
  InputDescription,
  Modal,
  PlusIcon,
  PrimaryButton,
  SecondaryButton,
  Spinner,
  TextInput,
} from '@movingimage-evp/mi-ui-component-library';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Theme, useCreateThemeMutation } from 'src/generated/graphql-manager';
import { useCurrentUser } from 'src/manager/hooks/current-user';

import { GET_LSPRO_BRANDING_SETTINGS } from './branding.api';
import { themePreviewVar } from './theme-preview';

import styles from './branding.module.css';

export function NewThemeModal() {
  const { t } = useTranslation();
  const { lsproId } = useCurrentUser();
  const [modalVisible, setModalVisible] = useState(false);
  const [name, setName] = useState('');

  const [createTheme, { loading }] = useCreateThemeMutation();

  const handleAddNewTheme = (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();

    if (!name || loading) return;

    createTheme({
      variables: { input: { lsproId, name } },
      optimisticResponse: {
        createTheme: {
          __typename: 'CreateThemeSuccess',
          theme: {
            __typename: 'AppearanceTheme',
            id: `temp-${Date.now()}`,
            name,
            mode: Theme.DARK,
            isDefault: false,
            assets: [],
            appearanceColors: [],
            fonts: {
              __typename: 'AppearanceFonts',
              headingFont: '',
              textFont: '',
            },
          },
        },
      },
      update: (cache, { data }) => {
        if (data?.createTheme.__typename !== 'CreateThemeSuccess') return;

        const cachedData = cache.readQuery<GetLsproBrandingSettingsQuery>({
          query: GET_LSPRO_BRANDING_SETTINGS,
          variables: { lsproId },
        });

        if (cachedData?.lsproSettings?.themes) {
          cache.writeQuery({
            query: GET_LSPRO_BRANDING_SETTINGS,
            variables: { lsproId },
            data: {
              ...cachedData,
              lsproSettings: {
                ...cachedData.lsproSettings,
                themes: [...cachedData.lsproSettings.themes, data.createTheme.theme],
              },
            },
          });
        }
      },
      onCompleted: (data) => {
        if (data?.createTheme.__typename !== 'CreateThemeSuccess') return;

        setName('');
        setModalVisible(false);
        themePreviewVar({ themeId: data.createTheme.theme.id });
      },
    });
  };

  return (
    <>
      <PrimaryButton data-testid="add-new-theme-button" onClick={() => setModalVisible(true)}>
        {t('manager.branding.newTheme.addButton')} <PlusIcon />
      </PrimaryButton>

      <Modal
        data-testid="new-theme-modal"
        title={t('manager.branding.newTheme.modal.title')}
        isOpen={modalVisible}
        onClose={() => setModalVisible(false)}
        className={styles.themeModal}
        footer={
          <>
            <SecondaryButton data-testid="new-theme-cancel-button" type="button" onClick={() => setModalVisible(false)}>
              {t('common.cancel')}
            </SecondaryButton>

            <PrimaryButton data-testid="new-theme-save-button" disabled={!name} form="new-theme-form">
              {loading ? <Spinner /> : t('manager.branding.newTheme.modal.saveButton')}
            </PrimaryButton>
          </>
        }
      >
        <form id="new-theme-form" onSubmit={handleAddNewTheme}>
          <InputDescription inputId="theme-name-input" label={t('manager.branding.newTheme.modal.nameLabel')}>
            <TextInput
              data-testid="theme-name-input"
              placeholder={t('manager.branding.newTheme.modal.namePlaceholder')}
              autoFocus
              value={name}
              onChange={(event) => setName(event.target.value)}
            />
          </InputDescription>
        </form>
      </Modal>
    </>
  );
}
